import {
  canGenerateUUID,
  generateUUID,
  addBroadcastEventListener,
  sendBroadcastEvent,
} from '../../../utils'
import { KeycloakRealm } from '../types'
import instances from './instances'
import environment from '../../../config'
import { getIdToken } from './userService'
import { getIdTokenFromStorage, setIdTokenInStorage } from './storageService'

export const redirectLogin = (realm: KeycloakRealm): void => {
  const authUrl = `${environment.auth.keycloak.url}/realms/${realm}/protocol/openid-connect/auth`

  const queryParams: Record<string, string> = {
    'client_id': environment.auth.keycloak.clientId,
    'redirect_uri': `${window.location.origin}/?sso=${realm}`,
    'response_mode': 'fragment',
    'response_type': 'code',
    'scope': 'openid email profile',
  }

  if (canGenerateUUID()) {
    queryParams.nonce = generateUUID()
    queryParams.state = generateUUID()
  }

  const loginUrl = `${authUrl}?${new URLSearchParams(queryParams).toString()}`

  window.location.assign(loginUrl)
}

export const redirectLogout = (): void => {
  if (instances.active === null)
    throw new Error('[KEYCLOAK] Is not possible to logout an user that is not authenticated.')

  const logoutUrl = instances.active.createLogoutUrl({
    redirectUri: window.location.origin,
  })
  window.location.assign(logoutUrl)
}

export const updateToken = async (): Promise<void> => {
  const minValidityInSecondsFromNow: number = 10
  if (!instances.active?.isTokenExpired(minValidityInSecondsFromNow)) return
  try {
    await instances.active?.updateToken(minValidityInSecondsFromNow)
    updateIdTokenInStorage()
  } catch (err) {
    instances.active?.clearToken()
    console.error('[KEYCLOAK] Access token refresh finished with error.')
  }
}

export const renewSession = (): void => {
  if (instances.active === null)
    throw new Error(
      '[KEYCLOAK] Is not possible to renew an user session when user is not authenticated.',
    )

  const isSessionStillValid = 
    instances.active.authenticated && 
    instances.active.token && 
    !instances.active.isTokenExpired(5)

  if (isSessionStillValid) {
    console.log('[KEYCLOAK] False logout trigger detected. Session is still valid. Ignoring logout event.')
    return
  }

  console.log('[KEYCLOAK] Session terminated. Redirecting to login page...')
  window.location.assign(window.location.origin + '/login')
}

export const handleOtherTabsLogin = (): void => {
  addBroadcastEventListener('login', (payload) => {
    const { realm } = payload as { realm: KeycloakRealm }

    if (!instances?.active?.authenticated) window.location.reload()
    else if (instances.active?.realm !== realm) redirectLogout()
  })
}

export const notifyOtherTabsAboutLogin = (): void =>
  sendBroadcastEvent('login', { realm: instances.active?.realm })

export const updateIdTokenInStorage = (): void => {
  const idToken = getIdToken()
  const storageIdToken = getIdTokenFromStorage()

  console.log('[KEYCLOAK] Updating user identification token in local storage...')

  if (idToken !== null) updateExistentIdTokenInStorage(idToken, storageIdToken)
  else updateInexistentIdTokenInStorage(storageIdToken)

  console.log('[KEYCLOAK] User identification token in local storage was updated successfully.')
}

const updateExistentIdTokenInStorage = (idToken: string, storageIdToken: string | null): void => {
  if (storageIdToken !== null && idToken !== storageIdToken) {
    console.log(
      '[KEYCLOAK] User identification token in local storage is deprecated, refreshing it...',
    )
    setIdTokenInStorage(idToken)

  } else if (storageIdToken === null) {
    console.log('[KEYCLOAK] User identification token in local storage is missing, inserting it...')
    setIdTokenInStorage(idToken)
  } else {
    console.log('[KEYCLOAK] User identification token in local storage is up-to-date.')
  }
}

const updateInexistentIdTokenInStorage = (storageIdToken: string | null): void => {
  if (storageIdToken !== null) {
    console.log('[KEYCLOAK] User identification token in local storage is expired, removing it...')
    setIdTokenInStorage(null)
  }
}
